import React from 'react';
import { Controller, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { navBarColors, buttonsLinksColors, mobileBackgroundColors } from 'shared/constants/menu-colors';
import { Select } from 'shared/components';
import { MobileAppFlair } from 'types/graphql';
import { ActiveSwitch } from 'src/components/active-switch';
import { InlineBanner } from 'src/components/inline-banner';
import ImageUploadField from 'src/dispensary-admin/settings/mobile-app/image-upload-field';
import { useStores } from 'src/hooks/use-stores';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { uploadImage } from 'shared/graphql/misc/mutations';
import { radiusOptions, productCardButtonOptions } from '../helpers';
import { MenuCustomizationLayout } from '../components/menu-customization-layout';
import { PreviewSwitcher } from '../components/preview-switcher';
import { OptionPicker } from '../components/option-picker';
import { ColorPickerV2, ACCESSIBLE_COLORS_BANNER } from '../components/color-picker-v2';
import { ProductCardButtonPicker } from '../components/product-card-button-picker';

import { usePreview } from '../components/preview/use-preview';
import { useApplyToAllLocations } from '../use-apply-to-all-locations';
import { useThemeForm } from './use-theme-form';

import { ThemeFormProps } from './theme.types';
import { Heading, Input, Inputs, Label, SubText } from '../customize.styles';

export function ThemeForm(props: ThemeFormProps): JSX.Element {
  const sectionName = 'Theme';
  const { control, handlePublish, handleReset, isDirty, previewData, applyAllInput, afterApplyAll } = useThemeForm(
    props
  );
  const { handleApplyToLocations, hasAccessToAllLocations, chainId, totalLocationCount } = useApplyToAllLocations({
    input: applyAllInput,
    afterApplyAll,
    sectionName,
  });
  const flags = useFlags();
  const { apolloClient } = useStores();

  const { theme } = usePreview(previewData);
  const hasMobileApp = useHasMobileApp();

  const tabShowFieldNames: Record<
    string,
    'mobileTabShowCart' | 'mobileTabShowLoyalty' | 'mobileTabShowScanPay' | 'mobileTabShowSpecials'
  > = {
    specials: 'mobileTabShowSpecials',
    pay: 'mobileTabShowScanPay',
    cart: 'mobileTabShowCart',
    loyalty: 'mobileTabShowLoyalty',
  };

  const showPay = flags['fintech.retail-payments.scan-to-pay.rollout'] ?? false;
  const showMobileRedesign = flags['ecomm.app.v2-redesign'] ?? false;
  const mobileHeaderImageField = showMobileRedesign ? 'mobileHeaderImageUrlV2' : 'mobileHeaderImageUrl';

  const tabKeysToDefaults: Record<string, string> = {
    shop: 'Shop',
    specials: 'Specials',
    ...(showPay ? { pay: 'Pay' } : {}),
    cart: 'Cart',
    ...(showMobileRedesign ? { loyalty: 'Loyalty', more: 'More' } : { account: 'Account' }),
  };
  const showSpecials = useWatch({ control, name: 'mobileTabShowSpecials' });
  const showScanPay = useWatch({ control, name: 'mobileTabShowScanPay' });
  const showCart = useWatch({ control, name: 'mobileTabShowCart' });
  const showLoyalty = useWatch({ control, name: 'mobileTabShowLoyalty' });
  const showTabLabels = useWatch({ control, name: 'mobileShowTabLabels' });
  const enabledToggleableTabs =
    Number(Boolean(showSpecials)) +
    Number(Boolean(showCart)) +
    Number(Boolean(showLoyalty)) +
    Number(showPay && Boolean(showScanPay));
  const hasReachedMaxEnabledTabs = enabledToggleableTabs >= 3;

  return (
    <MenuCustomizationLayout
      headerText={sectionName}
      subHeaderText='Select how main elements such as buttons, cards and fields will appear.'
      isDirty={isDirty}
      handlePublish={() => void handlePublish()}
      handleReset={handleReset}
      previewComponent={<PreviewSwitcher previewData={previewData} />}
      handleApplyToLocations={handleApplyToLocations}
      hasAccessToAllLocations={hasAccessToAllLocations}
      chainId={chainId}
      totalLocationCount={totalLocationCount}
    >
      <Inputs>
        <Input>
          <Controller
            name='defaultRadius'
            control={control}
            render={({ onChange, value }) => (
              <OptionPicker label='Style' options={radiusOptions} onChange={onChange} selectedOption={value} />
            )}
          />
        </Input>
      </Inputs>

      <Heading>Colors</Heading>
      <SubText>Select colors to express your brand.</SubText>

      <Inputs>
        <Input>
          <Controller
            name='navBar'
            control={control}
            render={({ onChange, value }) => (
              <ColorPickerV2
                label='Navigation Bar'
                colors={navBarColors}
                onSelect={onChange}
                selectedColor={value}
                allowCustomColorSelection
              />
            )}
          />
        </Input>

        <Input>
          <Controller
            name='buttonsLinks'
            control={control}
            render={({ onChange, value }) => (
              <ColorPickerV2
                label='Buttons and Links'
                colors={buttonsLinksColors}
                onSelect={onChange}
                selectedColor={value}
              />
            )}
          />
        </Input>

        <Input>
          <Controller
            name='productCardButton'
            control={control}
            render={({ onChange, value }) => (
              <ProductCardButtonPicker
                label='Product Card Button'
                options={productCardButtonOptions}
                onChange={onChange}
                selectedOption={value}
                theme={theme}
              />
            )}
          />
        </Input>

        <Input>
          <Controller
            name='staffPickTag'
            control={control}
            render={({ onChange, value }) => (
              <ColorPickerV2
                label='Staff Pick Tag'
                colors={buttonsLinksColors}
                onSelect={onChange}
                selectedColor={value}
              />
            )}
          />
        </Input>

        <Input>
          <Controller
            name='discountTag'
            control={control}
            render={({ onChange, value }) => (
              <ColorPickerV2
                label='Discount Tag'
                colors={buttonsLinksColors}
                onSelect={onChange}
                selectedColor={value}
              />
            )}
          />
        </Input>
      </Inputs>

      {hasMobileApp && (
        <>
          <Heading>Mobile App</Heading>
          <SubText>Customize mobile app colors and styles.</SubText>

          <Inputs>
            <Input>
              {showMobileRedesign ? (
                <Controller
                  name='mobileBackgroundColor'
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerV2
                      label='Background Color'
                      colors={mobileBackgroundColors}
                      onSelect={onChange}
                      selectedColor={value}
                    />
                  )}
                />
              ) : (
                <Controller
                  name='mobileHeaderBackgroundColor'
                  control={control}
                  render={({ onChange, value }) => (
                    <ColorPickerV2
                      label='Navigation Bar'
                      colors={navBarColors}
                      onSelect={onChange}
                      selectedColor={value}
                    />
                  )}
                />
              )}
            </Input>

            <Input>
              <Label>Header Image</Label>
              <Controller
                name={mobileHeaderImageField}
                control={control}
                render={({ onChange, value }) => (
                  <ImageUploadField
                    fieldId={mobileHeaderImageField}
                    helperText='Less than 500x500px, .png only without background'
                    image={value}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    handleUpload={async (image) => {
                      const res = await apolloClient.mutate({
                        mutation: uploadImage,
                        variables: { input: { image } },
                      });

                      if (res.data?.uploadImage?.url) {
                        onChange(res.data.uploadImage.url);
                      }
                    }}
                  />
                )}
              />
            </Input>

            {showMobileRedesign && (
              <>
                <Input>
                  <Label htmlFor='mobileBackgroundFlair'>Background Decoration</Label>
                  <Controller
                    name='mobileBackgroundFlair'
                    control={control}
                    render={({ onChange, value }) => (
                      <Select id='mobileBackgroundFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                        <option value={MobileAppFlair.none}>None</option>
                        <option value={MobileAppFlair.angled}>Angled</option>
                        <option value={MobileAppFlair.curved}>Curved</option>
                        <option value={MobileAppFlair.jagged}>Jagged</option>
                        <option value={MobileAppFlair.wavy}>Wavy</option>
                      </Select>
                    )}
                  />
                </Input>

                <Input>
                  <Label htmlFor='mobileButtonFlair'>Button Decoration</Label>
                  <Controller
                    name='mobileButtonFlair'
                    control={control}
                    render={({ onChange, value }) => (
                      <Select id='mobileButtonFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                        <option value={MobileAppFlair.none}>None</option>
                        <option value={MobileAppFlair.angled}>Angled</option>
                        <option value={MobileAppFlair.curved}>Curved</option>
                        <option value={MobileAppFlair.jagged}>Jagged</option>
                        <option value={MobileAppFlair.wavy}>Wavy</option>
                      </Select>
                    )}
                  />
                </Input>

                <Input>
                  <Label htmlFor='mobileDividerFlair'>Divider Decoration</Label>
                  <Controller
                    name='mobileDividerFlair'
                    control={control}
                    render={({ onChange, value }) => (
                      <Select id='mobileDividerFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                        <option value={MobileAppFlair.none}>None</option>
                        <option value={MobileAppFlair.angled}>Angled</option>
                        <option value={MobileAppFlair.curved}>Curved</option>
                        <option value={MobileAppFlair.jagged}>Jagged</option>
                        <option value={MobileAppFlair.wavy}>Wavy</option>
                      </Select>
                    )}
                  />
                </Input>

                <Input>
                  <Label htmlFor='mobileLoadingFlair'>Loading Decoration</Label>
                  <Controller
                    name='mobileLoadingFlair'
                    control={control}
                    render={({ onChange, value }) => (
                      <Select id='mobileLoadingFlair' onChange={onChange} value={value} width='100%' fontSize={13}>
                        <option value={MobileAppFlair.none}>None</option>
                        <option value={MobileAppFlair.angled}>Angled</option>
                        <option value={MobileAppFlair.curved}>Curved</option>
                        <option value={MobileAppFlair.jagged}>Jagged</option>
                        <option value={MobileAppFlair.wavy}>Wavy</option>
                      </Select>
                    )}
                  />
                </Input>
              </>
            )}

            <Heading>Mobile App Tabs</Heading>
            {showMobileRedesign && <SubText>Select up to 5 tabs to display</SubText>}

            <Input>
              <Controller
                name='mobileNavigationBarBackgroundColor'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerV2 label='Tab Bar' colors={navBarColors} onSelect={onChange} selectedColor={value} />
                )}
              />
            </Input>

            <Input>
              <Controller
                name='mobileNavigationBarActiveColor'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerV2 label='Active Tab' colors={navBarColors} onSelect={onChange} selectedColor={value} />
                )}
              />
            </Input>

            <Input>
              <Controller
                name='mobileNavigationBarInactiveColor'
                control={control}
                render={({ onChange, value }) => (
                  <ColorPickerV2 label='Inactive Tab' colors={navBarColors} onSelect={onChange} selectedColor={value} />
                )}
              />
            </Input>

            {showMobileRedesign && (
              <Input>
                <Label>Show Tab Labels</Label>
                <Controller
                  name='mobileShowTabLabels'
                  control={control}
                  render={({ onChange, value }) => (
                    <ActiveSwitch name='mobileShowTabLabels' active={value} onClick={onChange} hasLabel />
                  )}
                />
              </Input>
            )}

            {Object.entries(tabKeysToDefaults).map(([key, humanized]) => (
              <TabWrapper key={key}>
                {(showTabLabels || !showMobileRedesign) && (
                  <Input>
                    <Label htmlFor={`mobileTabLabel${humanized}`}>{humanized} Tab Label</Label>
                    <Controller
                      name={`mobileTabLabel${humanized}`}
                      control={control}
                      render={({ onChange, value }) => (
                        <StyledInput
                          type='text'
                          id={`mobileTabLabel${humanized}`}
                          value={value}
                          onChange={(e) => onChange(e.target.value)}
                          placeholder={humanized}
                        />
                      )}
                    />
                  </Input>
                )}

                {showMobileRedesign &&
                  (key !== 'more' && key !== 'shop' ? (
                    <Input>
                      <Label htmlFor={`mobileTabShow${humanized}`}>Show {humanized}</Label>
                      <Controller
                        name={tabShowFieldNames[key]}
                        control={control}
                        render={({ onChange, value }) => (
                          <ToggleRow>
                            <ActiveSwitch
                              active={value}
                              disabled={hasReachedMaxEnabledTabs && !value}
                              onClick={onChange}
                              usePropForValue
                            />
                          </ToggleRow>
                        )}
                      />
                    </Input>
                  ) : (
                    <Input>
                      <Label htmlFor={`mobileTabShow${humanized}`}>Show {humanized}</Label>
                      <ToggleRow>
                        <ActiveSwitch active disabled onClick={() => undefined} usePropForValue />
                      </ToggleRow>
                    </Input>
                  ))}
              </TabWrapper>
            ))}
          </Inputs>
        </>
      )}

      <StyledInlineBanner severity='info' title='Important!' description={ACCESSIBLE_COLORS_BANNER} />
    </MenuCustomizationLayout>
  );
}

const StyledInlineBanner = styled(InlineBanner)`
  width: 100%;
`;

const StyledInput = styled.input`
  border: 1px solid #d3d8de;
  border-radius: 4px;
  padding: 8px;
  width: 100%;
  max-width: 310px;
`;

const ToggleRow = styled.div`
  align-items: center;
  display: flex;
  min-height: 22px;
`;

const TabWrapper = styled.div`
  display: flex;
  gap: 10px;

  > div:first-child {
    flex: 4 0 0px;
  }

  > div:last-child {
    flex: 2 0 0px;
  }
`;
